'use client';

import Link from 'next/link';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { useCartStore } from '@/ui/store/cart';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { LayoutContentQuery } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { screenBreakpoints } from '@/constants/screenBreakpoints';
import { useSubnavData } from '@/ui/providers/subnavDataProvider';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { Banner } from '../banner/banner';
import NukiLogo from '../../../../../public/images/header/nuki-logo.svg';
import QuestionMarkIcon from '../../../../../public/images/header/question-mark.svg.svg';
import CartIcon from '../../../../../public/images/header/cart.svg';
import HeaderDesktopLink from './components/HeaderDesktopLink';
import HeaderUserButton from './components/header-user-button/header-user-button';
import { HeaderMobileMenu } from './components/header-mobile-menu/header-mobile-menu';
import MyAccountNavigation from '../../pages/my-account/components/MyAccountNavigation';
import SubNav from '../../SubNav/SubNav';
import { BannerTile } from '../banner/types/banner-tile';
import { shouldHideHeaderOnPage } from './utils';

export type HeaderContent = LayoutContentQuery['header'];

export type HeaderProps = {
  headerData: HeaderContent;
  bannerData: BannerTile;
};

export default function Header({ headerData, bannerData }: Readonly<HeaderProps>) {
  const t = useTranslations('Header');
  const localization = useLocalization();
  const currentPath = usePathname();
  const { localizedLink } = useLocaleLink();
  const { toggleCartDisplay, numberOfItems, items } = useCartStore();
  const [numberOfCartItems, setNumberOfCartItems] = useState(0);
  const [showHeader, setShowHeader] = useState(true); // State to control header visibility
  const [showSubnav, setShowSubnav] = useState(false); // State to control subnav visibility
  const { subnavData } = useSubnavData();

  const { scrollY } = useScroll();
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScreenMobile, setIsScreenMobile] = useState(true);

  const [showClosingOverlay, setShowClosingOverlay] = useState(false);
  const [closeOverlayFunction, setCloseOverlayFunction] = useState<() => void>(() => () => {});

  useEffect(() => {
    if (window.innerWidth > screenBreakpoints.lg) {
      setIsScreenMobile(false);
    }
  }, []);

  useEffect(() => {
    setNumberOfCartItems(numberOfItems());
  }, [items, numberOfCartItems, numberOfItems]);

  const getHelpCenterLink = (): string => {
    const helpcenterLink = t('HelpLink');

    // the base english helplink got en-001 as language
    // so we replace it for the united states
    if (localization.areaCode === 'us') {
      return helpcenterLink.replace('en-001', 'en-us');
    }
    return `${helpcenterLink}/${localization.language}`;
  };

  // When user scrolls down, hide the header and show the subnav if
  // the user scrolls up show the header and hide the subnav.
  // Legacy shapes need to be ignored, therefore only 'subnav-v2' is allowed
  const subnavVisibility = !!subnavData && subnavData?.shape?.identifier === 'subnav-v2';
  useMotionValueEvent(scrollY, 'change', (latest) => {
    if (subnavVisibility && !isScreenMobile) {
      if (latest > lastScrollY + 20) {
        setShowHeader(false);
        setShowSubnav(true);
        setLastScrollY(latest);
      } else if (latest < lastScrollY - 20) {
        setShowHeader(true);
        setShowSubnav(false);
        setLastScrollY(latest);
      }
    }
  });
  useEffect(() => {
    if (!subnavVisibility && !subnavData) {
      setShowHeader(true);
    }
  }, [showHeader, subnavVisibility, subnavData]);

  // Hide the header on checkout page, US landing page, and additional paths
  if (shouldHideHeaderOnPage(currentPath)) {
    return null;
  }

  return (
    <header className="sticky top-0 z-40 px-4 pt-6 sm:px-8">
      <Banner tile={bannerData} isInHeader />
      {/* Showing the overlay like this because it's not possible inside the HeaderUserButton because of stacking
      when animations are applied in the nav element im not able to apply fixed position to the overlay
      */}
      {showClosingOverlay && <div onClick={closeOverlayFunction} className="fixed inset-0" />}
      <AnimatePresence mode="popLayout" initial={false}>
        {showHeader && (
          <motion.nav
            initial={{ y: -150 }}
            animate={{ y: 0 }}
            exit={{ y: -150 }}
            transition={{ duration: 0.5 }}
            className="relative mx-auto flex h-14 max-w-screen-2xl items-center justify-between rounded-md bg-white shadow-nuki-blured sm:h-20"
          >
            <Link
              href={localizedLink('/')}
              className="pl-4 sm:pl-6"
              data-tile-element-id="nav-logo"
            >
              <Image
                src={NukiLogo}
                alt="logo"
                width={100}
                height={50}
                sizes="(max-width: 640px) 100px, 200px"
                className="h-auto w-18 sm:w-25"
              />
            </Link>
            <ul className="hidden h-full lg:flex">
              {headerData?.children?.map((link) => (
                <HeaderDesktopLink key={link.id} link={link} />
              ))}
            </ul>
            <div className="flex h-full items-center">
              <Link
                href={getHelpCenterLink()}
                className="flex h-full items-center px-2 duration-200 ease-in hover:bg-neutral-5"
                aria-label={t('help')}
                data-tile-element-id="nav-help"
              >
                <Image src={QuestionMarkIcon} alt="" width={24} height={24} aria-hidden="true" />
              </Link>

              <HeaderUserButton
                setShowClosingOverlay={setShowClosingOverlay}
                setCloseOverlayFunction={setCloseOverlayFunction}
              />
              <button
                type="button"
                onClick={toggleCartDisplay}
                className="relative flex h-full items-center px-2 duration-200 ease-in hover:bg-neutral-5 lg:rounded-r-md lg:pr-6"
                aria-label={t('Cart')}
                data-tile-element-id="nav-cart"
              >
                <div className="relative">
                  <Image src={CartIcon} alt="" width={24} height={24} aria-hidden="true" />
                  {numberOfCartItems > 0 && (
                    <div className="absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 rounded-md bg-primary-100 px-[5px] py-0.5 text-xs font-bold text-neutral-100">
                      {numberOfCartItems}
                    </div>
                  )}
                </div>
              </button>
              <HeaderMobileMenu headerData={headerData} />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <AnimatePresence mode="popLayout" initial={false}>
        {showSubnav && (
          <motion.div
            initial={{ y: -150, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { delay: 0.5, duration: 0.5 } }}
            exit={{ y: -150 }}
            transition={{ duration: 0.5 }}
          >
            <SubNav />
          </motion.div>
        )}
      </AnimatePresence>

      <MyAccountNavigation />
    </header>
  );
}
